import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useLocation,
  useRouterState,
} from "@tanstack/react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Suspense, type ReactNode } from "react";
import { BlogTranslationProvider } from "@/contexts/BlogTranslationContext";
import TrailingSlashRedirect from "@/components/TrailingSlashRedirect";
import SpaLinkInterceptor from "@/components/SpaLinkInterceptor";

import RouteScrollToTop from "@/components/RouteScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import SpaPageViewTracker from "@/components/SpaPageViewTracker";
import { CONSENT_BOOTSTRAP_SCRIPT, GTM_ID } from "@/lib/consent";
import NotFound from "@/pages/NotFound";
import { AnimatePresence, motion } from "framer-motion";
import appCss from "@/styles.css?url";
// Self-hosted variable Open Sans. Only the latin subsets are preloaded: they cover
// the visible above-the-fold text on every EN/ES page, and the variable file
// serves every weight 300-800 from one request.
import openSansLatin from "@fontsource-variable/open-sans/files/open-sans-latin-wght-normal.woff2?url";
import openSansLatinExt from "@fontsource-variable/open-sans/files/open-sans-latin-ext-wght-normal.woff2?url";

import { buildHreflangAlternates, getHtmlLang, normalizePath } from "@/lib/seo/hreflang";
import { resolveCanonicalPath } from "@/lib/seo/canonicalAliases";
import { markdownMirrorPath } from "@/lib/markdownMirror";
import { SITE_URL } from "@/config/site";

const PageTransition = ({ children }: { children: ReactNode }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.15, ease: "easeInOut" }}
  >
    {children}
  </motion.div>
);

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (
    <html lang={getHtmlLang(pathname)} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const location = useLocation();
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BlogTranslationProvider>
            <TrailingSlashRedirect />
            <SpaLinkInterceptor />
            <SpaPageViewTracker />

            <RouteScrollToTop />
            <AnimatePresence mode="wait">
              <PageTransition key={location.pathname}>
                <Outlet />
              </PageTransition>
            </AnimatePresence>
            <Suspense fallback={null}>
              <CookieConsent />
            </Suspense>
          </BlogTranslationProvider>
        </TooltipProvider>
    </QueryClientProvider>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  loader: ({ location }) => ({ pathname: location.pathname }),
  head: ({ loaderData }: { loaderData?: { pathname?: string } }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { title: "QbD Group — Life Sciences Consulting Experts" },
      { name: "description", content: "QbD Group — 600+ life sciences experts in regulatory affairs, quality assurance, clinical trials & compliance consulting." },
      { name: "theme-color", content: "#0a1628" },
      { name: "google-site-verification", content: "41m4BZgWt6zu1n4u99zGscjwCeuMpy7QKzr-iOQ7nvE" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}${normalizePath(loaderData?.pathname ?? "/") === "/" ? "" : normalizePath(loaderData?.pathname ?? "/")}` },
      { property: "og:title", content: "QbD Group — Life Sciences Consulting Experts" },
      { property: "og:description", content: "QbD Group — 600+ life sciences experts in regulatory affairs, quality assurance, clinical trials & compliance consulting." },
      { property: "og:image", content: "https://qbdgroup.com/og-image.png" },
      { property: "og:site_name", content: "QbD Group" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "QbD Group — Life Sciences Consulting Experts" },
      { name: "twitter:description", content: "QbD Group — 600+ life sciences experts in regulatory affairs, quality assurance, clinical trials & compliance consulting." },
      { name: "twitter:image", content: "https://qbdgroup.com/og-image.png" },
    ],
    links: [
      // Fonts first: preload the woff2 before the stylesheet so the browser can
      // fetch it in parallel instead of waiting for CSS to discover the @font-face.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: openSansLatin,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: openSansLatinExt,
        crossOrigin: "anonymous",
      },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "32x32", type: "image/x-icon" },
      { rel: "icon", href: "/favicon-16x16.png", sizes: "16x16", type: "image/png" },
      { rel: "icon", href: "/favicon-32x32.png", sizes: "32x32", type: "image/png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://jrtdedcfhvzaomneervp.supabase.co", crossOrigin: "anonymous" },

      { rel: "alternate", type: "application/rss+xml", title: "QbD Group — Blog RSS Feed", href: "https://qbdgroup.com/rss.xml?feed=blog" },
      { rel: "alternate", type: "application/rss+xml", title: "QbD Group — Regulatory Updates RSS Feed", href: "https://qbdgroup.com/rss.xml?feed=regulatory" },
      // Plain-Markdown mirror of article bodies, for AI assistants that fetch
      // pages without executing JavaScript.
      ...(markdownMirrorPath(normalizePath(loaderData?.pathname ?? "/"))
        ? [
            {
              rel: "alternate",
              type: "text/markdown",
              title: "Markdown version",
              href: `${SITE_URL}${markdownMirrorPath(normalizePath(loaderData?.pathname ?? "/"))}`,
            },
          ]
        : []),
      // Canonical + hreflang live here (once per document): TanStack concatenates
      // `links` across every matched route, so nested routes would duplicate them.
      {
        rel: "canonical",
        href: `${SITE_URL}${resolveCanonicalPath(normalizePath(loaderData?.pathname ?? "/"))}`,
      },

      ...buildHreflangAlternates(loaderData?.pathname ?? "/", SITE_URL).map(({ hreflang, href }) => ({
        rel: "alternate",
        hrefLang: hreflang,
        href,
      })),
    ],
    scripts: [
      // Google Consent Mode defaults + stored-consent update + GTM. Must run
      // before any other script; all vendor tags (GA4, Ahrefs, HubSpot,
      // LinkedIn, Leadfeeder, Google Ads) live inside GTM.
      { children: CONSENT_BOOTSTRAP_SCRIPT },
      // Sitewide entity graph. AI answer engines resolve "QbD Group" from these
      // nodes on whichever page they land on, not just the homepage. The
      // homepage renders its own richer Organization node, so skip it there to
      // avoid two nodes sharing one @id.
      ...(["/", "/en", "/es", "/zh-cn"].includes(normalizePath(loaderData?.pathname ?? "/"))
        ? []
        : [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@type": "Organization",
                    "@id": `${SITE_URL}/#organization`,
                    name: "QbD Group",
                    url: SITE_URL,
                    logo: `${SITE_URL}/assets/qbd-logo.webp`,
                    sameAs: [
                      "https://www.linkedin.com/company/qbd-group/",
                      "https://www.youtube.com/@QbDGroup",
                    ],
                  },
                  {
                    "@type": "WebSite",
                    "@id": `${SITE_URL}/#website`,
                    url: SITE_URL,
                    name: "QbD Group",
                    publisher: { "@id": `${SITE_URL}/#organization` },
                    inLanguage: getHtmlLang(loaderData?.pathname ?? "/"),
                  },
                ],
              }),
            },
          ]),
    ],

  }),
});
